<script setup lang="ts">
  import { computed, h, ref, watch, onMounted } from 'vue'
  import { Button } from '@/components/ui/button'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import { MoreVertical, MoveRight } from 'lucide-vue-next'
  import { useTranslation } from '@/composables/useTranslation'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore.ts'
  import { caseTypesDE } from '@/i18n/caseTypes'
  import router from '@/router'
  import {
    StateEnum,
    type CustomerCase as Case,
    CasesService,
    FlagEnum,
    RelationshipTypeEnum,
    EntityTypeEnum
  } from '@/types/generated'
  import LegacyIntegrationButtons from '@/components/app/cases/legacy/LegacyIntegrationButtons.vue'
  import { ROUTES } from '@/router/routes.ts'
  import { useToast } from '@/components/ui/toast'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@/components/ui/alert-dialog'

  const { t } = useTranslation({
    en: {
      aiChat: 'AI Chat',
      cancel: 'Cancel',
      completed: 'COMPLETED',
      completeRequest: 'Complete Request',
      deleteRequest: 'Delete Request',
      deleteRequestConfirmation: 'Are you sure you want to delete this request?',
      deleteRequestWarning:
        'Are you sure you want to delete this request? This action cannot be undone.',
      deleteRequestError: 'Failed to delete the case. Please try again.',
      delete: 'Delete',
      downloadAllDocuments: 'Download All Documents',
      entities: 'Entities',
      error: 'Error',
      general: 'General',
      inProgress: 'IN PROGRESS',
      lawSpecificData: 'Law Specific Data',
      new: 'NEW',
      noDocuments: 'No Documents',
      noDocumentsForDownload: 'No documents available for download.',
      rejectRequest: 'Reject Request',
      rejected: 'REJECTED',
      reopenRequest: 'Set Request to "Open"',
      requestFrom: 'Request from',
      requestInProgress: 'Request in Progress',
      statusUpdateError: 'Status could not be updated.',
      documents: 'Documents'
    },
    de: {
      aiChat: 'KI-Chat',
      cancel: 'Abbrechen',
      completed: 'ABGESCHLOSSEN',
      completeRequest: 'Anfrage abschließen',
      deleteRequest: 'Anfrage löschen',
      deleteRequestConfirmation: 'Sind Sie sicher, dass Sie diese Anfrage löschen möchten?',
      deleteRequestWarning: 'Dieser Vorgang kann nicht rückgängig gemacht werden.',
      deleteRequestError:
        'Die Anfrage konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.',
      delete: 'Löschen',
      downloadAllDocuments: 'Alle Dokumente herunterladen',
      entities: 'Beteiligte',
      error: 'Fehler',
      general: 'Allgemein',
      inProgress: 'IN BEARBEITUNG',
      lawSpecificData: 'Rechtsspezifische Daten',
      new: 'NEU',
      noDocuments: 'Keine Dokumente',
      noDocumentsForDownload: 'Keine Dokumente zum Herunterladen verfügbar.',
      rejectRequest: 'Anfrage ablehnen',
      rejected: 'ABGELEHNT',
      reopenRequest: 'Anfrage auf "offen" setzen',
      requestFrom: 'Anfrage von',
      requestInProgress: 'Anfrage in Bearbeitung',
      statusUpdateError: 'Status konnte nicht aktualisiert werden.',
      documents: 'Dokumente'
    }
  })

  const caseStore = useCaseStore()
  const { toast } = useToast()
  const caseDetailPageV3ApiFlag = useFeatureFlag(FlagEnum.CASE_DETAIL_PAGE_V3_API)
  const newPdfEditorEnabled = useFeatureFlag(FlagEnum.NEW_PDF_EDITOR)

  const isUpdatingCaseStatus = ref(false)
  const isDeleteDialogOpen = ref(false)

  // Local state for immediate UI updates
  const localCaseStatus = ref<StateEnum | null>(null)

  const caseRecord = computed<Case | null>(() => caseStore.caseRecord)
  const nonEmptyLawSpecificMappers = computed(() => caseStore.nonEmptyLawSpecificMappers)
  const entities = computed(() => caseRecord.value?.data?.entities)

  // Reset local state when case record changes
  watch(
    () => caseRecord.value?.state,
    (newState) => {
      // Only reset local state if we're not in the middle of an update
      if (!isUpdatingCaseStatus.value) {
        localCaseStatus.value = null
      }
    }
  )

  const caseClientName = computed(() => {
    if (!caseRecord.value?.data?.entities) return ''

    const clientEntity = caseRecord.value.data.entities.find((entity) =>
      entity.relationships.some(
        (rel) => rel.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT
      )
    )

    if (!clientEntity?.entity) return ''

    const entity = clientEntity.entity

    let entityName = ''
    if (entity.last_name) {
      if (entity.first_name && entity.first_name.length > 0) {
        const firstNameChar = entity.first_name.charAt(0).toUpperCase()
        entityName = `${firstNameChar}. ${entity.last_name}`.trim()
      } else {
        entityName = entity.last_name
      }
    } else if (entity.first_name) {
      entityName = entity.first_name
    } else {
      entityName = ''
    }

    switch (entity.entity_type) {
      case EntityTypeEnum.NATURAL_PERSON: {
        return entityName
      }
      case EntityTypeEnum.LEGAL_PERSON:
        return entity.company_name || entityName || ''
      case EntityTypeEnum.INSTITUTION:
        return entity.institution_name || entityName || ''
      default:
        return entityName || ''
    }
  })

  const translatedCaseType = computed(() => {
    if (!caseRecord.value?.type) return ''
    return caseTypesDE[caseRecord.value.type as keyof typeof caseTypesDE] || caseRecord.value.type
  })

  const formattedCreationDate = computed(() => {
    if (!caseRecord.value?.created_at) return ''
    const date = new Date(caseRecord.value.created_at)
    return (
      date
        .toLocaleString('de-DE', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
        .replace(',', ' um') + ' Uhr'
    )
  })

  const caseStatus = computed(() => {
    // Use local state if available, otherwise use the case record state
    const currentState = localCaseStatus.value || caseRecord.value?.state
    if (!currentState) return 'new'

    switch (currentState) {
      case StateEnum.COMPLETED:
        return 'completed'
      case StateEnum.IN_PROGRESS:
        return 'inProgress'
      case StateEnum.REJECTED:
        return 'rejected'
      default:
        return 'new'
    }
  })

  const statusBadgeClass = computed(() => {
    switch (caseStatus.value) {
      case 'completed':
        return 'bg-green-600'
      case 'inProgress':
        return 'bg-[#17a2b8]'
      case 'rejected':
        return 'bg-red-600'
      default:
        return 'bg-black'
    }
  })

  const pages = computed(() => {
    // Determine the correct route for the General tab based on feature flag
    const generalRoute = caseDetailPageV3ApiFlag.isEnabled.value
      ? ROUTES.APP.CASES.LEGACY.CASE_DETAIL.replace(':uuid', caseRecord.value?.uuid ?? '')
      : `/cases/${caseRecord.value?.id ?? ''}/`

    const enableLawSpecificData = caseRecord.value?.org?.enable_law_specific_data ?? false

    const tabs = [
      {
        title: () => h('span', { 'data-cy': 'tab-allgemein-text' }, t('general')),
        route: generalRoute
      }
    ]

    if (enableLawSpecificData) {
      tabs.push(
        {
          title: () =>
            h('span', { class: 'flex gap-2 items-center' }, [
              t('lawSpecificData'), // Text for the second page
              nonEmptyLawSpecificMappers.value.length > 0
                ? h(
                    'span',
                    { class: 'badge-square', 'data-cy': 'lawSpecificDataBadge' },
                    `${nonEmptyLawSpecificMappers.value.length}`
                  )
                : null
            ]),
          route: ROUTES.APP.CASES.CASE_DETAIL.LAW_SPECIFIC_DATA.replace(
            ':uuid',
            caseRecord.value?.uuid ?? ''
          )
        },
        {
          title: () =>
            h('span', { class: 'flex gap-2 items-center' }, [
              t('entities'),
              entities.value && entities.value?.length > 0
                ? h('span', { class: 'badge-square' }, `${entities.value.length}`)
                : null
            ]),
          route: ROUTES.APP.CASES.CASE_DETAIL.ENTITIES.replace(
            ':uuid',
            caseRecord.value?.uuid ?? ''
          ).replace('/:entityUuid?', '')
        }
      )
    }

    if (newPdfEditorEnabled.isEnabled.value) {
      tabs.push({
        title: () => h('span', t('documents')),
        route: ROUTES.APP.CASES.CASE_DETAIL.DOCUMENTS.replace(':uuid', caseRecord.value?.uuid ?? '')
      })
    }

    if (enableLawSpecificData) {
      tabs.push({
        title: () => h('span', t('aiChat')),
        route: ROUTES.APP.CASES.CASE_DETAIL.AI_CHAT.replace(':uuid', caseRecord.value?.uuid ?? '')
      })
    }

    return tabs
  })

  const activePage = computed(() => {
    const currentPath = router.currentRoute.value.fullPath
    return pages.value.find((page) => {
      // For entities page, match both with and without entityUuid
      if (page.route.includes('entities')) {
        return currentPath.includes('entities')
      }
      // For law-specific-data page
      if (page.route.includes('law-specific-data')) {
        return currentPath.includes('law-specific-data')
      }
      // For chat page
      if (page.route.includes('chat')) {
        return currentPath.includes('chat')
      }
      // For general page
      return page.route === currentPath
    })
  })

  const isCaseCompleted = computed(() => {
    // Use local state if available, otherwise use the case record state
    const currentState = localCaseStatus.value || caseRecord.value?.state
    return currentState === StateEnum.COMPLETED
  })

  const updateCaseStatus = async (status: StateEnum) => {
    if (!caseRecord.value?.uuid) return

    try {
      isUpdatingCaseStatus.value = true

      // Update local state immediately for responsive UI
      localCaseStatus.value = status

      await CasesService.casesPartialUpdate({
        uuid: caseRecord.value.uuid,
        requestBody: {
          state: status
        }
      })

      // Reload the case to get the updated state
      await caseStore.loadCase(caseRecord.value.uuid)

      // Clear local state after successful update
      localCaseStatus.value = null
    } catch (error) {
      console.error(`Error updating case status to ${status}:`, error)
      // Revert local state on error
      localCaseStatus.value = caseRecord.value?.state || null

      // Show error toast
      toast({
        title: t('error'),
        description: t('statusUpdateError'),
        variant: 'destructive'
      })
    } finally {
      isUpdatingCaseStatus.value = false
    }
  }

  const completeCase = () => updateCaseStatus(StateEnum.COMPLETED)
  const reopenCase = () => updateCaseStatus(StateEnum.NEW)
  const rejectRequest = () => updateCaseStatus(StateEnum.REJECTED)
  const markRequestInProgress = () => updateCaseStatus(StateEnum.IN_PROGRESS)
  const deleteRequest = async () => {
    if (!caseRecord.value?.uuid) return

    try {
      await CasesService.casesDestroy({
        uuid: caseRecord.value.uuid
      })
      isDeleteDialogOpen.value = false
      // Navigate to case overview after successful deletion. Use window.location for full page reload to Django route
      window.location.href = '/cases/'
    } catch (error) {
      console.error('Error deleting case:', error)
      toast({
        title: t('error'),
        description: t('deleteRequestError'),
        variant: 'destructive'
      })
    }
  }

  const downloadAllDocuments = async () => {
    if (!caseRecord.value?.uuid) return

    try {
      const response = await CasesService.casesDownloadDocumentsZipRetrieve({
        uuid: caseRecord.value.uuid
      })

      // Create a blob from the response and trigger download
      const blob = new Blob([response as unknown as Blob], { type: 'application/zip' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `jupus_export_${caseRecord.value.data?.client?.last_name || 'case'}.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading documents:', error)
      // Check if it's a 404 error (no documents)
      if ((error as any).status === 404) {
        toast({
          title: t('noDocuments'),
          description: t('noDocumentsForDownload'),
          variant: 'destructive'
        })
      } else {
        toast({
          title: 'Fehler',
          description: 'Beim Herunterladen der Dokumente ist ein Fehler aufgetreten.',
          variant: 'destructive'
        })
      }
    }
  }

  const getTabId = (route: string) => {
    if (route.includes('entities')) return 'entities'
    if (route.includes('law-specific-data')) return 'law-specific-data'
    if (route.includes('chat')) return 'ai-chat'
    return 'allgemein'
  }

  const handleNavigation = (route: string) => {
    // We need to handle navigation differently depending on the destination:
    if (route.startsWith('/cases/')) {
      // OLD FRONTEND NAVIGATION (/cases/:id)
      // Use window.location.href because:
      // 1. The old frontend is a separate Vue app with its own router
      // 2. Routes like /cases/:id don't exist in this SPA's route configuration
      // 3. Using router.push() would cause Vue Router warnings and fail to navigate
      // 4. window.location.href triggers a full page reload to load the old frontend
      window.location.href = route
    } else {
      router.push(route)
    }
  }

  // Check the feature flags when the component mounts
  onMounted(async () => {
    await caseDetailPageV3ApiFlag.checkFlag()
    await newPdfEditorEnabled.checkFlag()
  })
</script>

<template>
  <div class="flex flex-row flex-wrap items-start justify-between pt-4 md:flex-nowrap">
    <div class="ml-0 mt-2 h-auto grow">
      <div
        id="title"
        class="position-relative align-items-center justify-content-start flex gap-3"
      >
        <h1 class="text-h1 font-weight-bold text-nowrap">
          {{ t('requestFrom') }} {{ caseClientName }}
        </h1>
        <span
          class="badge"
          :class="statusBadgeClass"
        >
          {{ t(caseStatus) }}
        </span>
      </div>
      <p class="text-sm text-stone-500">
        {{ translatedCaseType }}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          aria-hidden="true"
          data-slot="icon"
          class="inline size-4"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3"
          ></path>
        </svg>
        Erstellt am {{ formattedCreationDate }}
      </p>
    </div>
    <div
      id="options"
      class="my-3 flex h-full items-start justify-end gap-2"
    >
      <LegacyIntegrationButtons :case-record="caseRecord" />
      <Button
        v-if="!isCaseCompleted"
        variant="default"
        class="h-9 bg-[#0ca678] text-white hover:bg-[#0a8e67]"
        :disabled="isUpdatingCaseStatus"
        @click="completeCase"
      >
        {{ isUpdatingCaseStatus ? '...' : t('completeRequest') }}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            class="h-9 min-w-10 border border-gray-300 bg-white"
          >
            <MoreVertical class="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            v-if="(localCaseStatus || caseRecord?.state) !== StateEnum.NEW"
            @click="reopenCase"
          >
            {{ t('reopenRequest') }}
          </DropdownMenuItem>
          <DropdownMenuItem
            v-if="(localCaseStatus || caseRecord?.state) !== StateEnum.REJECTED"
            @click="rejectRequest"
          >
            {{ t('rejectRequest') }}
          </DropdownMenuItem>
          <DropdownMenuItem
            v-if="(localCaseStatus || caseRecord?.state) !== StateEnum.IN_PROGRESS"
            @click="markRequestInProgress"
          >
            {{ t('requestInProgress') }}
          </DropdownMenuItem>
          <DropdownMenuItem
            class="text-red-500 focus:bg-red-50 focus:text-red-600"
            @click="isDeleteDialogOpen = true"
          >
            {{ t('deleteRequest') }}
          </DropdownMenuItem>
          <DropdownMenuItem @click="downloadAllDocuments">
            {{ t('downloadAllDocuments') }}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </div>

  <AlertDialog v-model:open="isDeleteDialogOpen">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ t('deleteRequestConfirmation') }}</AlertDialogTitle>
        <AlertDialogDescription>{{ t('deleteRequestWarning') }}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel @click="isDeleteDialogOpen = false">{{ t('cancel') }}</AlertDialogCancel>
        <AlertDialogAction @click="deleteRequest">{{ t('delete') }}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>

  <!-- Navigation section - full width below header -->
  <div class="w-full border-b border-gray-200 pb-0">
    <nav class="flex w-full flex-wrap gap-x-1 overflow-hidden pt-1">
      <button
        v-for="(page, index) in pages"
        :key="page.route"
        :data-tab="getTabId(page.route)"
        :data-cy="`tab-${getTabId(page.route)}`"
        class="flex-shrink-0 flex-grow-0 cursor-pointer items-center gap-x-1 border-b-2 py-2 text-sm
          hover:text-purple-600 focus:text-purple-600 focus:outline-none disabled:pointer-events-none
          disabled:opacity-50 md:gap-x-2 md:text-sm"
        :class="[
          page.route === activePage?.route
            ? 'border-purple-600 font-semibold text-purple-600'
            : 'border-transparent',
          index === 0 ? 'pr-1' : 'px-1'
        ]"
        @click="handleNavigation(page.route)"
      >
        <component :is="page.title" />
      </button>
    </nav>
  </div>
</template>
